import * as React from 'react';
import { useState } from 'react';
import useForkRef from '@mui/utils/useForkRef';
import {
  DateRangePicker,
  type DateRangePickerProps,
} from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import type { SingleInputDateRangeFieldProps } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import { FieldType } from '@mui/x-date-pickers-pro/models';
import { Box, Button, useMediaQuery, InputAdornment } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  mobilePrimaryDatePickerButtonSx,
  pageHeaderDatePickerTextFieldSx,
  widgetNarrowDateRangeButtonSx,
} from './sharedDatePickerSx';
import {
  dateRangeFilterOptions,
  DataRangeFilterEnum,
  getDataRangeFilterFromLabel,
  getDateRangeFromFilter,
} from '@/constants/dates';
import {
  mobileWidth,
  mobileWidthValue,
  pickerDesktopModeMediaQuery,
} from '@/constants';
import dayjs, { Dayjs } from '@/config/dayjs';
import { APP_DATE_FORMAT, parseAppDate } from '@/utils/date';
import {
  DateRangeValidationError,
  MultiInputDateRangeField,
  PickerChangeHandlerContext,
} from '@mui/x-date-pickers-pro';
import {
  PickersShortcutsItemContext,
  PickersShortcutsProps,
} from '@mui/x-date-pickers/PickersShortcuts';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { useLocalization } from '@/hooks/useLocalization';
import { EMBEDDABLE_PICKER_DESKTOP_MODE_MEDIA_QUERY } from '@/integrations/embeddableWidgetPortalUtils';
import { useWidgetPortalContainer } from '@/integrations/embeddableWidgetPortalContext';
import { useEmbeddablePickerPortalSlotProps } from '@/integrations/useEmbeddablePickerPortalSlotProps';
import { isCapacitorApp } from '@/utils/isCapacitorApp';
import { usePickerContext } from '@mui/x-date-pickers/hooks';
import PickerShortcutsSelect from './PickerShortcutsSelect';
import { capShortcutsAt } from './capShortcutsAt';

/** Custom field: centered primary outlined button opens the range dialog (Account flyout mobile). */
interface MobilePrimaryIconDateRangeFieldProps extends SingleInputDateRangeFieldProps {
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  rangeText?: string;
}

type MobilePrimaryIconDateRangeFieldComponent = ((
  props: MobilePrimaryIconDateRangeFieldProps &
    React.RefAttributes<HTMLDivElement>,
) => React.JSX.Element) & { fieldType?: FieldType };

const MobilePrimaryIconDateRangeField = React.forwardRef(
  function MobilePrimaryIconDateRangeField(
    props: MobilePrimaryIconDateRangeFieldProps,
    ref: React.Ref<HTMLDivElement>,
  ) {
    const {
      setOpen,
      label,
      disabled,
      id,
      rangeText,
      inputProps: { 'aria-label': ariaLabel } = {},
    } = props;

    const { rootRef, triggerRef } = usePickerContext();
    const handleRootRef = useForkRef(rootRef, ref);

    return (
      <Box
        ref={handleRootRef}
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Button
          ref={triggerRef}
          variant='outlined'
          color='primary'
          size='medium'
          disabled={disabled}
          id={id}
          type='button'
          aria-label={
            typeof ariaLabel === 'string' && ariaLabel
              ? ariaLabel
              : typeof label === 'string'
                ? label
                : undefined
          }
          onClick={() => setOpen?.(true)}
          sx={
            rangeText
              ? widgetNarrowDateRangeButtonSx
              : mobilePrimaryDatePickerButtonSx
          }
        >
          <DateRangeIcon fontSize='medium' />
          {rangeText}
        </Button>
      </Box>
    );
  },
) as unknown as MobilePrimaryIconDateRangeFieldComponent;

MobilePrimaryIconDateRangeField.fieldType = 'single-input';

/** One entry in the picker's shortcut list. */
export interface DateRangeShortcut {
  label: string;
  getValue: () => [Dayjs, Dayjs];
}

interface DateRangeProps {
  startDate?: string;
  endDate?: string;
  onChange?: (
    startDate: string,
    endDate: string,
    shortcutLabel?: string,
    shortcutFilter?: DataRangeFilterEnum,
  ) => void;
  label?: string;
  useSingleInput?: boolean;
  calendars?: 1 | 2 | 3;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  shouldDisableDate?: (day: Dayjs) => boolean;
  shortcutsFilter?: (filter: DataRangeFilterEnum) => boolean;
  /**
   * Replaces the app-wide presets with the caller's own, for a widget whose
   * ranges are not in {@link DataRangeFilterEnum}. `shortcutsFilter` does not
   * apply to these; `onChange` reports the chosen item's label as usual.
   */
  shortcutItems?: DateRangeShortcut[];
  /**
   * The shortcut the dates came from, for a caller that restores a saved
   * choice: the picker only remembers a shortcut picked while it is mounted,
   * so it would otherwise open on its placeholder. An empty string means the
   * dates were picked by hand. Leave it unset to let the picker track this.
   */
  selectedShortcutLabel?: string;
  /** Called when the picker closes, however it was closed. */
  onClose?: () => void;
  useMobilePrimaryIconButton?: boolean;
  useWidgetNarrowIconField?: boolean;
  disabled?: boolean;
}

export default function DateRange({
  startDate,
  endDate,
  onChange,
  label,
  useSingleInput = true,
  calendars = 2,
  minDate,
  maxDate,
  shouldDisableDate,
  shortcutsFilter,
  shortcutItems,
  selectedShortcutLabel,
  onClose,
  useMobilePrimaryIconButton = false,
  useWidgetNarrowIconField = false,
  disabled = false,
}: DateRangeProps) {
  const { t } = useLocalization();
  const embeddablePortalSlotProps = useEmbeddablePickerPortalSlotProps();
  const widgetPortalContainer = useWidgetPortalContainer();
  const desktopModeMediaQuery = widgetPortalContainer
    ? EMBEDDABLE_PICKER_DESKTOP_MODE_MEDIA_QUERY
    : pickerDesktopModeMediaQuery;
  const isMobileWidth = useMediaQuery(mobileWidth);
  const useIconButtonMobile =
    (useMobilePrimaryIconButton && isMobileWidth) || useWidgetNarrowIconField;
  const value: [Dayjs | null, Dayjs | null] = [
    parseAppDate(startDate),
    parseAppDate(endDate),
  ];

  const [start, end] = value;
  const widgetNarrowRangeText =
    useWidgetNarrowIconField && start && end
      ? `${start.format(APP_DATE_FORMAT)} – ${end.format(APP_DATE_FORMAT)}`
      : undefined;

  const [open, setOpen] = useState(false);
  const [lastShortcutSelected, setLastShortcutSelected] = useState<
    PickersShortcutsItemContext | undefined
  >(undefined);
  const shownShortcutLabel =
    selectedShortcutLabel === undefined
      ? lastShortcutSelected?.label
      : selectedShortcutLabel || undefined;

  const MobileShortcuts = (
    props: PickersShortcutsProps<[dayjs.Dayjs | null, dayjs.Dayjs | null]>,
  ) => {
    return (
      <PickerShortcutsSelect
        items={props.items}
        placeholder={label ?? t('dateRange')}
        selectedLabel={shownShortcutLabel}
        onSelect={(item) => {
          const nextValue = item.getValue({ isValid: () => true });
          const [start, end] = nextValue;
          if (!start || !end) return;

          setLastShortcutSelected(item);
          onChange?.(
            start.format(APP_DATE_FORMAT),
            end.format(APP_DATE_FORMAT),
            item.label,
            getDataRangeFilterFromLabel(item.label),
          );
          setOpen(false);
          onClose?.();
        }}
      />
    );
  };

  const keepWithinLimits = (date: Dayjs | null): Dayjs | null => {
    if (!date?.isValid()) return date;
    if (maxDate && date.isAfter(maxDate, 'day')) return maxDate;
    if (minDate && date.isBefore(minDate, 'day')) return minDate;
    return date;
  };

  const handleChange = (
    value: [dayjs.Dayjs | null, dayjs.Dayjs | null],
    context: PickerChangeHandlerContext<DateRangeValidationError>,
  ) => {
    setLastShortcutSelected(context.shortcut);
    const startDate = keepWithinLimits(value[0]);
    const endDate = keepWithinLimits(value[1]);
    const shortcutFilter = context.shortcut?.label
      ? getDataRangeFilterFromLabel(context.shortcut.label)
      : undefined;
    onChange?.(
      startDate?.format(APP_DATE_FORMAT) || '',
      endDate?.format(APP_DATE_FORMAT) || '',
      context.shortcut?.label,
      shortcutFilter,
    );
  };

  const appWideShortcuts: DateRangeShortcut[] = dateRangeFilterOptions
    .filter((option) =>
      shortcutsFilter ? shortcutsFilter(option.filter) : true,
    )
    .flatMap((option) => {
      const dateRange = getDateRangeFromFilter(option.filter);
      const start = parseAppDate(dateRange.startDate);
      const end = parseAppDate(dateRange.endDate);
      if (!start || !end) return [];
      return [{ label: option.label, getValue: () => [start, end] }];
    });

  const shortcuts = capShortcutsAt(shortcutItems ?? appWideShortcuts, maxDate);

  const dateRangePickerSlotProps = {
    shortcuts: {
      items: shortcuts,
    },
    ...(useIconButtonMobile
      ? {
          field: { setOpen, disabled, rangeText: widgetNarrowRangeText },
        }
      : {
          textField: {
            label: shownShortcutLabel,
            disabled,
            fullWidth: true,
            sx: [
              (theme: Theme) => ({
                width: 250,
                // Match `mobileWidth` / `useWidgetWidthIsBelow(..., dashboardMobileWidthValue)` (920px).
                [theme.breakpoints.down(mobileWidthValue)]: { width: 50 },
              }),
              pageHeaderDatePickerTextFieldSx,
            ] as SxProps<Theme>,
            inputProps: isCapacitorApp() ? { readOnly: true } : undefined,
            InputProps: {
              endAdornment: (
                <InputAdornment
                  position='end'
                  onClick={() => {
                    if (!disabled) {
                      setOpen(true);
                    }
                  }}
                  sx={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                >
                  <DateRangeIcon />
                </InputAdornment>
              ),
            },
          },
        }),
    actionBar: {
      actions: ['clear', 'cancel', 'accept'] as const,
      sx: {
        justifyContent: 'center',
      },
    },
    ...embeddablePortalSlotProps,
    dialog: {
      ...embeddablePortalSlotProps.dialog,
      sx: (theme: Theme) => ({
        '& .MuiDialog-container .MuiPaper-root': {
          boxSizing: 'border-box',
          padding: '4vh 4px !important',
        },
        [theme.breakpoints.down(mobileWidthValue)]: {
          '& .MuiDialog-container .MuiPaper-root': {
            width: '100% !important',
            maxWidth: '100% !important',
            display: 'block !important',
            margin: 0,
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100% !important',
            maxHeight: 'none !important',
            boxSizing: 'border-box',
            padding: '70px 0px 0px !important',
          },
          '& .MuiPickersLayout-root': {
            display: 'block !important',
          },
          '& .MuiDialogActions-root': {
            paddingTop: '0px !important',
            paddingBottom: '0px !important',
          },
          '& .MuiPickersLayout-contentWrapper': {
            display: 'flex',
            justifyContent: 'center',
          },
          '& .MuiDateRangeCalendar-root': {
            marginLeft: 'auto',
            marginRight: 'auto',
          },
        },
      }),
    },
  } as unknown as NonNullable<DateRangePickerProps['slotProps']>;

  return (
    <Box sx={{ width: '100%' }}>
      <DateRangePicker
        label={label ?? t('dateRange')}
        value={value}
        format={APP_DATE_FORMAT}
        onChange={handleChange}
        disabled={disabled}
        enableAccessibleFieldDOMStructure={!isCapacitorApp()}
        desktopModeMediaQuery={desktopModeMediaQuery}
        open={open}
        onOpen={() => {
          if (!disabled) {
            setOpen(true);
          }
        }}
        onClose={() => {
          setOpen(false);
          onClose?.();
        }}
        minDate={minDate}
        maxDate={maxDate}
        shouldDisableDate={shouldDisableDate}
        calendars={useIconButtonMobile ? 1 : calendars}
        slots={{
          field: useIconButtonMobile
            ? MobilePrimaryIconDateRangeField
            : useSingleInput
              ? SingleInputDateRangeField
              : MultiInputDateRangeField,
          shortcuts: useIconButtonMobile ? MobileShortcuts : undefined,
          toolbar: () => null,
        }}
        slotProps={dateRangePickerSlotProps}
      />
    </Box>
  );
}
